import { Badge, Box, Group, Tooltip } from '@mantine/core';
import { TechnologyExperience } from '../../../../../models/technologyExperience.model';
import { PersonTechnology } from '../../../../../types/technology';

interface SkillCloudProps {
  technologies: PersonTechnology[];
  technologyExperience: TechnologyExperience[];
}

// Helper function to get color by category
const getColorByCategory = (tech: PersonTechnology) => {
  if (!tech.technology.categories || tech.technology.categories.length === 0) {
    return 'gray';
  }

  const category = tech.technology.categories[0];

  // Define available Material Design colors from our palette
  const materialColors = [
    'materialBlue', 'materialTeal', 'materialOrange', 'materialPurple', 'materialRed',
    'materialAmber', 'materialCyan', 'materialGray', 'materialGreen', 'sopraBlue',
    'sopraPurple', 'sopraOrange', 'sopraGreen', 'materialDeepPurple', 'brand',
    'indigo', 'violet', 'grape', 'pink', 'lime', 'dark'
  ];

  // Use category ID if available for consistent color assignment
  if (category.id) {
    return materialColors[category.id % materialColors.length];
  }

  // Fallback: use category name hash for consistent color assignment
  const hash = category.name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return materialColors[hash % materialColors.length];
};

export function SkillCloud({ technologies, technologyExperience }: SkillCloudProps) {
  // Create a map of technology names to their experience
  const experienceMap = new Map(
    technologyExperience.map(exp => [exp.technologyName, exp.yearsOfExperience])
  );

  // Type for technologies with experience
  type TechWithExperience = PersonTechnology & { experience: number };

  // Separate technologies into those with experience and those without
  const techsWithExperience: TechWithExperience[] = technologies
    .filter(tech => experienceMap.has(tech.technology?.name))
    .map(tech => ({
      ...tech,
      experience: experienceMap.get(tech.technology?.name)!
    }))
    .sort((a, b) => b.experience - a.experience);

  const techsWithoutExperience = technologies
    .filter(tech => !experienceMap.has(tech.technology?.name))
    .sort((a, b) => (a.technology?.name || '').localeCompare(b.technology?.name || ''));

  // Combine: technologies with experience first, then technologies without
  const sortedTechnologies: (PersonTechnology | TechWithExperience)[] = [
    ...techsWithExperience,
    ...techsWithoutExperience
  ];

  // Colors for top 3 skills
  const topColors = ['violet', 'blue', 'cyan'];

  return (
    <Box py="xs">
      <Group gap="xs" align="center" style={{ flexWrap: 'wrap' }}>
        {sortedTechnologies.map((tech, index) => {
          const hasExperience = 'experience' in tech;
          const isTopSkill = hasExperience && index < 3;

          // Determine badge color
          let badgeColor: string;
          if (isTopSkill) {
            badgeColor = topColors[index];
          } else if (tech.favourite) {
            badgeColor = getColorByCategory(tech);
          } else {
            badgeColor = 'gray';
          }

          // Size hierarchy: lg for top 3, sm for next 2, xs for rest
          let badgeSize: 'lg' | 'sm' | 'xs' = 'xs';
          if (hasExperience) {
            if (index < 3) {
              badgeSize = 'lg';
            } else if (index < 5) {
              badgeSize = 'sm';
            }
          }

          return (
            <Tooltip
              key={tech.id}
              label={tech.technology?.categories?.map(c => c.name).join(', ') || 'No category'}
              position="top"
              withArrow
            >
              <Badge
                size={badgeSize}
                color={badgeColor}
                variant="filled"
                radius="md"
                style={{
                  margin: '2px',
                  cursor: 'default'
                }}
              >
                {tech.technology?.name || 'Unknown Technology'}
                {hasExperience && (tech as TechWithExperience).experience !== undefined && ` (${(tech as TechWithExperience).experience.toFixed(1)}y)`}
              </Badge>
            </Tooltip>
          );
        })}
      </Group>
    </Box>
  );
}

